import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Box, Stack, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { getDatabase, ref, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import app from '../../services/firebase';
import * as yup from 'yup';

function ContactResponseForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = getAuth(app);
    const database = getDatabase(app);


    const { contactItem } = location.state || {};

    const [emailBody, setEmailBody] = useState('');
    const [projectReviewStatus, setProjectReviewStatus] = useState(contactItem?.projectReview || 'New');
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Redirect if no contact item is passed
    useEffect(() => {
        const checkAuthAndPrefill = async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    navigate('/auth/signon'); // Redirect to sign-on if not logged in
                    return;
                }

                // Force a token refresh to get the latest custom claims
                const idTokenResult = await user.getIdTokenResult(true);
                const isAdmin = idTokenResult.claims.admin;

                if (!isAdmin) {
                    setSubmitError("Access denied: Admins only.");
                    setTimeout(() => navigate('/'), 5000); // Redirect after showing error
                    return;
                }

                if (!contactItem) {
                    navigate('/admin/contactregistrylist'); // Redirect back to the admin list
                } else {
                    // Pre-fill email body with some basic info
                    setEmailBody(`Dear ${contactItem.firstname} ${contactItem.lastname},\n\nRegarding your project: "${contactItem.projectTitle}"\n\n`);
                }
            } catch (err) {
                console.error("Error checking auth or pre-filling form:", err);
                setSubmitError("An error occurred while verifying your permissions.");
            }
        };

        checkAuthAndPrefill();
    }, [contactItem, navigate, auth]);

    const validationSchema = yup.object().shape({
        emailBody: yup.string().min(10, "Email body must be at least 10 characters").required("Email body is required"),
        projectReviewStatus: yup.string().required("Project review status is required"),
    });

    const handleEmailBodyChange = (e) => {
        setEmailBody(e.target.value);
        if (errors.emailBody) {
            setErrors(prev => ({ ...prev, emailBody: '' }));
        }
    };

    const handleStatusChange = (e) => {
        setProjectReviewStatus(e.target.value);
        if (errors.projectReviewStatus) {
            setErrors(prev => ({ ...prev, projectReviewStatus: '' }));
        }
    };

    const handleSendResponse = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);

            await validationSchema.validate({ emailBody, projectReviewStatus }, { abortEarly: false });

            // Simulate sending email (in a real app, this would involve a backend call)
            console.log("--- Sending Email Response ---");
            console.log("To:", contactItem.email);
            console.log("Subject: Response to your project request - " + contactItem.projectTitle);
            console.log("Body:\n", emailBody);
            console.log("------------------------------");

            // Update projectReview status in Firebase
            const contactRef = ref(database, `contactRegistry/${contactItem.id}`);
            await update(contactRef, { projectReview: projectReviewStatus });

            alert('Response sent and status updated successfully!');
            navigate('/admin/contactregistrylist'); // Go back to the admin list
        } catch (err) {
            if (err.inner) {
                const newErrors = {};
                err.inner.forEach(error => {
                    newErrors[error.path] = error.message;
                });
                setErrors(newErrors);
            }
            setSubmitError("Failed to send response or update status.");
            console.error("Error sending response:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!contactItem) {
        return <Typography>Loading contact details...</Typography>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                Respond to Project Request
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                From: {contactItem.firstname} {contactItem.lastname} ({contactItem.email})
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                Project Title: {contactItem.projectTitle}
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Description:</b> {contactItem.projectRfqDescription}
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                Submitted: {new Date(contactItem.dtAdded).toLocaleString()}
            </Typography>

            <Stack spacing={3} sx={{ mt: 3 }}>
                <FormControl fullWidth>
                    <InputLabel id="project-review-status-label">Project Review Status</InputLabel>
                    <Select
                        labelId="project-review-status-label"
                        value={projectReviewStatus}
                        label="Project Review Status"
                        onChange={handleStatusChange}
                        error={Boolean(errors.projectReviewStatus)}
                        helperText={errors.projectReviewStatus}
                    >
                        <MenuItem value="New">New</MenuItem>
                        <MenuItem value="In Progress">In Progress</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Archived">Archived</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Email Response Body"
                    multiline
                    rows={10}
                    value={emailBody}
                    onChange={handleEmailBodyChange}
                    error={Boolean(errors.emailBody)}
                    helperText={errors.emailBody}
                    fullWidth
                    variant="outlined"
                />

                {submitError && (
                    <Typography color="error" variant="body2">
                        {submitError}
                    </Typography>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendResponse}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Sending...' : 'Send Response & Update Status'}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/admin/contactregistrylist')}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

export default ContactResponseForm;